/**
 * Auth Hook
 *
 * Manages authentication state and provides auth operations.
 * Uses React Query for server state management.
 *
 * Usage:
 *   const { user, isLoading, signIn, signUp, signOut } = useAuth();
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { resolveApiUrl } from '../lib/api-base';

// ============================================================
// Types
// ============================================================

interface AuthUser {
  id: string;
  email: string;
  role: string;
  tenantId: string;
  emailVerified: boolean;
}

interface SignUpData {
  email: string;
  password: string;
}

interface SignInData {
  email: string;
  password: string;
}

// ============================================================
// API Functions
// ============================================================

// Token storage key
const TOKEN_KEY = 'auth_token';

/**
 * Get stored auth token from localStorage.
 */
export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Store auth token in localStorage.
 */
export function setAuthToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

/**
 * Clear auth token from localStorage.
 */
export function clearAuthToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * Create headers with auth token if available.
 */
export function getAuthHeaders(): HeadersInit {
  const token = getAuthToken();
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

/**
 * Get current user from session.
 */
async function fetchCurrentUser(): Promise<AuthUser | null> {
  try {
    const token = getAuthToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(resolveApiUrl('/auth/me'), {
      credentials: 'include', // Include cookies
      headers,
    });

    if (!response.ok) {
      if (response.status === 401) {
        clearAuthToken(); // Clear invalid token
        return null; // Not authenticated
      }
      throw new Error('Failed to fetch user');
    }

    const data = await response.json();
    return data.data.user;
  } catch (error) {
    console.error('[Auth] Failed to fetch user:', error);
    return null;
  }
}

/**
 * Sign up with email and password.
 */
async function signUpRequest(data: SignUpData): Promise<{ message: string; user: AuthUser }> {
  const response = await fetch(resolveApiUrl('/auth/signup'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Sign up failed');
  }

  return result.data;
}

/**
 * Sign in with email and password.
 */
async function signInRequest(data: SignInData): Promise<{ user: AuthUser }> {
  const response = await fetch(resolveApiUrl('/auth/signin'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Sign in failed');
  }

  // Store token in localStorage for cross-origin auth
  if (result.data.token) {
    setAuthToken(result.data.token);
  }

  return result.data;
}

/**
 * Sign out.
 */
async function signOutRequest(): Promise<void> {
  const token = getAuthToken();
  const headers: HeadersInit = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(resolveApiUrl('/auth/signout'), {
    method: 'POST',
    credentials: 'include',
    headers,
  });

  // Always clear token on signout
  clearAuthToken();

  if (!response.ok) {
    throw new Error('Sign out failed');
  }
}

/**
 * Request password reset.
 */
async function forgotPasswordRequest(email: string): Promise<void> {
  const response = await fetch(resolveApiUrl('/auth/forgot-password'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Request failed');
  }
}

// ============================================================
// useAuth Hook
// ============================================================

export function useAuth() {
  const queryClient = useQueryClient();

  // Query current user (checks session)
  const {
    data: user,
    isLoading,
    error: queryError,
    refetch: refetchUser,
  } = useQuery({
    queryKey: ['auth', 'user'],
    queryFn: fetchCurrentUser,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Sign up mutation
  const signUpMutation = useMutation({
    mutationFn: signUpRequest,
    onSuccess: () => {
      // Don't set user - they need to verify email first
    },
  });

  // Sign in mutation
  const signInMutation = useMutation({
    mutationFn: signInRequest,
    onSuccess: (data) => {
      // Update user in cache
      queryClient.setQueryData(['auth', 'user'], data.user);
    },
  });

  // Sign out mutation
  const signOutMutation = useMutation({
    mutationFn: signOutRequest,
    onSuccess: () => {
      // Clear user from cache
      queryClient.setQueryData(['auth', 'user'], null);
      // Invalidate all queries (clear any user-specific data)
      queryClient.invalidateQueries();
    },
  });

  // Forgot password mutation
  const forgotPasswordMutation = useMutation({
    mutationFn: forgotPasswordRequest,
  });

  return {
    // State
    user: user ?? null,
    isLoading,
    isAuthenticated: !!user,
    error: queryError,

    // Sign up
    signUp: signUpMutation.mutateAsync,
    isSigningUp: signUpMutation.isPending,
    signUpError: signUpMutation.error?.message,

    // Sign in
    signIn: signInMutation.mutateAsync,
    isSigningIn: signInMutation.isPending,
    signInError: signInMutation.error?.message,

    // Sign out
    signOut: signOutMutation.mutateAsync,
    isSigningOut: signOutMutation.isPending,

    // Forgot password
    forgotPassword: forgotPasswordMutation.mutateAsync,
    isSendingReset: forgotPasswordMutation.isPending,

    // Utilities
    refetchUser,
  };
}

// ============================================================
// Google OAuth
// ============================================================

/**
 * Redirect to Google OAuth login.
 */
export function redirectToGoogleAuth() {
  window.location.href = resolveApiUrl('/auth/google');
}
